import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { COLORS } from '../utils/constants';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { handleOAuthCallback } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      // Prevent multiple executions
      if (hasProcessed.current) return;
      hasProcessed.current = true;
      try {
        // Get the hash fragment from URL (Supabase returns tokens in hash)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));

        // Try to get tokens from hash
        let accessToken = hashParams.get('access_token');
        let refreshToken = hashParams.get('refresh_token');

        // If not in hash, try query params
        if (!accessToken) {
          const queryParams = new URLSearchParams(window.location.search);
          accessToken = queryParams.get('access_token');
          refreshToken = queryParams.get('refresh_token');
        }

        // Check for error in URL
        const errorParam = hashParams.get('error') || new URLSearchParams(window.location.search).get('error');
        const errorDescription = hashParams.get('error_description') || new URLSearchParams(window.location.search).get('error_description');

        if (errorParam) {
          setError(errorDescription || 'Authentication failed');
          setTimeout(() => navigate('/signin'), 3000);
          return;
        }

        if (!accessToken) {
          setError('No access token received from Google');
          setTimeout(() => navigate('/signin'), 3000);
          return;
        }

        // Send tokens to backend
        const result = await handleOAuthCallback(accessToken, refreshToken);

        if (result.success) {
          // Redirect to dashboard on success
          navigate('/dashboard');
        } else {
          setError(result.error || 'Failed to complete authentication');
          setTimeout(() => navigate('/signin'), 3000);
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError('An unexpected error occurred');
        setTimeout(() => navigate('/signin'), 3000);
      }
    };

    processCallback();
  }, []); // Empty deps - only run once on mount

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: COLORS.white,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{
        textAlign: 'center',
        padding: '40px'
      }}>
        {error ? (
          <>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px'
            }}>
              ⚠️
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: COLORS.black,
              marginBottom: '12px'
            }}>
              Authentication Error
            </h2>
            <p style={{
              fontSize: '16px',
              color: COLORS.gray,
              marginBottom: '20px'
            }}>
              {error}
            </p>
            <p style={{
              fontSize: '14px',
              color: COLORS.darkGray
            }}>
              Redirecting to sign in...
            </p>
          </>
        ) : (
          <>
            <div style={{
              width: '48px',
              height: '48px',
              border: `4px solid ${COLORS.lightGray}`,
              borderTop: `4px solid ${COLORS.blue}`,
              borderRadius: '50%',
              margin: '0 auto 24px',
              animation: 'spin 1s linear infinite'
            }} />
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: COLORS.black,
              marginBottom: '12px'
            }}>
              Completing Sign In...
            </h2>
            <p style={{
              fontSize: '16px',
              color: COLORS.gray
            }}>
              Please wait while we set up your account
            </p>
          </>
        )}

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default AuthCallback;
